import { useEffect, useRef, useState, useCallback } from "react";
import { Sparkles, Volume2, VolumeX, Pause, Play, X, RotateCcw } from "lucide-react";

const TTS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`;
const TTS_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type TourStep = {
  id: string;
  selector?: string | null; // single target (legacy)
  targets?: string[];       // multi-target — arrow cycles through these while audio plays
  view?: "seo" | "ai" | "ads";
  title: string;
  text: string;
  scrollToTop?: boolean;
  /** Fired once when the step becomes active (used to drive UI like tab switches) */
  action?: () => void;
};

const dispatchAdsTab = (tab: "overview" | "aria" | "performance" | "search" | "insights") =>
  window.dispatchEvent(new CustomEvent("orbit:ads-set-tab", { detail: tab }));
const dispatchAriaMode = (mode: "standard" | "advanced") =>
  window.dispatchEvent(new CustomEvent("orbit:aria-set-mode", { detail: mode }));

const SCRIPT: TourStep[] = [
  {
    id: "welcome",
    selector: null,
    title: "Welcome",
    text:
      "Welcome to OrbitRank. This workspace demonstrates how AI automates SEO, AI search visibility, and Google Ads management — all in one platform.",
  },
  { id: "seo-tab", selector: '[data-tour-section="seo"]', view: "seo", title: "SEO",
    text: "SEO — continuous content generation and on-page optimization, fully automated." },
  { id: "ai-tab", selector: '[data-tour-section="ai"]', view: "ai", title: "AI Search Visibility",
    text: "AI search visibility — how your business surfaces inside ChatGPT, Gemini, and AI-driven search." },
  { id: "ads-tab", selector: '[data-tour-section="ads"]', view: "ads", title: "Google Ads",
    text: "Google Ads management — Aria builds, structures, and optimizes complete campaigns from scratch." },
  { id: "gsc-intro", view: "seo", title: "Google Search Console",
    targets: ['[data-tour-id="gsc-card"]', '[data-tour-id="gsc-impressions"]', '[data-tour-id="gsc-clicks"]', '[data-tour-id="gsc-ctr"]', '[data-tour-id="gsc-position"]'],
    text: "OrbitRank connects directly with Google Search Console to monitor your real search performance in real time. Every click, impression, ranking movement, and visibility signal updates automatically inside the platform." },
  { id: "gsc-chart", view: "seo", title: "Live Trends",
    targets: ['[data-tour-id="gsc-chart"]'],
    text: "Live trend tracking makes it easy to monitor growth patterns, search visibility, click-through rate, and ranking changes over time." },
  { id: "visibility", view: "seo", title: "Visibility & Keywords",
    targets: ['[data-tour-id="pp-visibility"]', '[data-tour-id="pp-impressions"]', '[data-tour-id="pp-pages"]', '[data-tour-id="pp-keywords"]'],
    text: "The SEO engine continuously measures visibility, publishing activity, keyword progress, and ranking distribution." },
  { id: "ranking-engine", view: "seo", title: "Ranking Engine",
    targets: ['[data-tour-id="re-visibility"]', '[data-tour-id="re-avgpos"]', '[data-tour-id="re-impressions"]', '[data-tour-id="re-rows"]'],
    text: "The ranking engine continuously tracks priority keywords and automatically focuses on the highest-impact ranking opportunities. OrbitRank monitors keyword movement in real time, helping businesses understand which rankings are improving, declining, or becoming competitive." },
  { id: "ranking-progress", view: "seo", title: "Progress & Live Tasks",
    targets: ['[data-tour-id="ranking-progress"]', '[data-tour-id="live-tasks"]'],
    text: "Keyword groups are automatically categorized by ranking strength and movement. Behind the scenes, OrbitRank continuously publishes content, submits pages for indexing, and monitors SEO execution automatically." },
  { id: "ai-capture", view: "ai", title: "AI Capture Board", scrollToTop: true,
    targets: ['[data-tour-id="ai-capture-main"]', '[data-tour-id="ai-coverage"]'],
    text: "OrbitRank also captures real questions people ask AI systems and search engines about your business. The platform uses those search patterns to generate content opportunities designed for modern AI-driven search visibility." },

  /* ============================================================
     GOOGLE ADS / ARIA — cinematic sequence
  ============================================================ */
  { id: "ads-enter", view: "ads", title: "Google Ads", scrollToTop: true,
    selector: '[data-tour-section="ads"]',
    action: () => dispatchAdsTab("aria"),
    text: "Now let's step into Google Ads. ARIA is OrbitRank's AI Google Ads expert." },
  { id: "aria-intro", view: "ads", title: "Meet ARIA",
    targets: ['[data-tour-id="aria-title"]', '[data-tour-id="aria-suggested"]', '[data-tour-id="aria-input"]'],
    text: "As soon as a business joins OrbitRank, ARIA can build complete Google Ads campaigns automatically — campaign structure, ad groups, keywords, targeting, optimization, and ongoing management." },
  { id: "aria-modes", view: "ads", title: "Hands-off or hands-on",
    targets: ['[data-tour-id="aria-title"]'],
    text: "Businesses can stay completely hands-off, or take manual control of their campaigns using advanced mode." },
  { id: "aria-advanced", view: "ads", title: "Advanced Mode",
    targets: ['[data-tour-id="aria-workspace"]', '[data-tour-id="aria-title"]'],
    action: () => dispatchAriaMode("advanced"),
    text: "Advanced mode unlocks deeper campaign control, live optimization workflows, AI-generated strategy recommendations, and direct campaign editing." },
  { id: "ads-performance", view: "ads", title: "Performance", scrollToTop: true,
    targets: ['[data-tour-id="perf-chart"]', '[data-tour-id="perf-metrics"]'],
    action: () => dispatchAdsTab("performance"),
    text: "OrbitRank connects directly with Google Ads to monitor live campaign performance in real time — spend, click-through rate, conversions, and return on ad spend." },
  { id: "ads-search", view: "ads", title: "Search Terms", scrollToTop: true,
    targets: ['[data-tour-id="search-terms-table"]'],
    action: () => dispatchAdsTab("search"),
    text: "ARIA continuously analyzes search behavior to identify valuable keywords, wasted spend, and new growth opportunities." },
  { id: "ads-insights", view: "ads", title: "Insights", scrollToTop: true,
    targets: ['[data-tour-id="insights-grid"]'],
    action: () => dispatchAdsTab("insights"),
    text: "The insights engine summarizes campaign performance, optimization opportunities, AI recommendations, and strategic growth signals — automatically." },
  { id: "outro", view: "seo", title: "OrbitRank",
    selector: null,
    scrollToTop: true,
    action: () => {
      dispatchAdsTab("overview");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    text: "We built OrbitRank to make growth feel simpler, faster, and more intelligent for modern businesses. If you'd like to experience the full platform yourself, we'd love to have you join us. OrbitRank includes a 7-day free trial so you can explore everything at your own pace. Thanks for exploring OrbitRank with us — and we hope to see you inside soon." },
];

const STORAGE_KEY = "orbitrank.demo.onboarding.v1";

type Phase = "idle" | "card" | "tour" | "done";
type VoiceStatus = "idle" | "preparing" | "loading" | "playing" | "paused" | "interrupted" | "ended" | "silent";

/** iOS Safari detection — used to apply calmer, lighter behavior on iPhone/iPad. */
const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = ua.includes("Macintosh") && (navigator as any).maxTouchPoints > 1;
  return iOSUA || iPadOS;
};

/** Use visualViewport.height on iOS so layout respects the dynamic Safari toolbar. */
const getViewportHeight = (): number => {
  if (typeof window === "undefined") return 0;
  return window.visualViewport?.height ?? window.innerHeight;
};

const getViewportMetrics = () => {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, offsetTop: 0, offsetLeft: 0 };
  }
  const vv = window.visualViewport;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
    offsetTop: vv?.offsetTop ?? 0,
    offsetLeft: vv?.offsetLeft ?? 0,
  };
};

const useTtsAudio = () => {
  const cacheRef = useRef<Map<string, string>>(new Map());

  const fetchAudio = useCallback(async (text: string): Promise<string | null> => {
    const cached = cacheRef.current.get(text);
    if (cached) return cached;
    try {
      const res = await fetch(TTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: TTS_ANON,
          Authorization: `Bearer ${TTS_ANON}`,
        },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        console.warn("[demo-onboarding] tts http error", res.status);
        return null;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      cacheRef.current.set(text, url);
      return url;
    } catch (e) {
      console.warn("[demo-onboarding] tts failed", e);
      return null;
    }
  }, []);

  return { fetchAudio };
};

/**
 * Premium animated arrow + glow ring pointing to a target rect.
 * No backdrop dim/blur — dashboard remains fully visible.
 */
const ArrowHighlight = ({ rect }: { rect: DOMRect | null }) => {
  if (!rect) return null;

  const ios = isIOS();
  const viewport = getViewportMetrics();
  const vh = viewport.height;
  const vw = viewport.width;

  const pad = ios ? 6 : 8;
  const controlsReserve = ios ? 146 : 120;
  const ringTop = Math.max(8, Math.min(rect.top - pad, Math.max(8, vh - controlsReserve - 24)));
  const ringLeft = Math.max(6, rect.left - pad);
  const ringW = Math.min(rect.width + pad * 2, Math.max(0, vw - ringLeft - 6));
  const ringH = Math.min(rect.height + pad * 2, Math.max(26, vh - controlsReserve - ringTop - 8));

  const targetCenterX = Math.max(30, Math.min(vw - 30, rect.left + rect.width / 2));
  // Distance from bottom of (visual) viewport — accounts for Safari toolbar.
  const arrowBottom = vh - rect.top + 14;
  const arrowMinFromTop = ios ? 56 : 70;
  // Reserve room for the controls bar + safe-area + breathing room so the arrow never lands on controls/chat.
  const arrowMaxBottom = vh - arrowMinFromTop - (ios ? 70 : 90);
  const finalBottom = Math.min(
    Math.max(controlsReserve, arrowBottom),
    Math.max(controlsReserve, arrowMaxBottom)
  );

  // Lighter visuals on iOS — fewer repaint-heavy shadows + smaller arrow.
  const ringBorder = ios ? "1px solid rgba(0,255,136,0.55)" : "1.5px solid rgba(0,255,136,0.65)";
  const ringShadow = ios
    ? "0 0 0 1px rgba(0,255,136,0.18), 0 0 16px 2px rgba(0,255,136,0.22)"
    : "0 0 0 1px rgba(0,255,136,0.25), 0 0 26px 4px rgba(0,255,136,0.32), 0 0 60px 10px rgba(139,92,246,0.18)";
  const ringBg = ios
    ? "transparent"
    : "radial-gradient(ellipse at center, rgba(0,255,136,0.08), transparent 70%)";
  // iOS: disable pulse loop entirely (no continuous repaint of glow).
  const ringAnim = ios ? "none" : "tourGlowPulse 2.4s ease-in-out infinite";
  const ringTransition = ios
    ? "top 900ms cubic-bezier(0.22,1,0.36,1), left 900ms cubic-bezier(0.22,1,0.36,1), width 900ms cubic-bezier(0.22,1,0.36,1), height 900ms cubic-bezier(0.22,1,0.36,1)"
    : "top 700ms cubic-bezier(0.22,1,0.36,1), left 700ms cubic-bezier(0.22,1,0.36,1), width 700ms cubic-bezier(0.22,1,0.36,1), height 700ms cubic-bezier(0.22,1,0.36,1)";

  const arrowW = ios ? 30 : 44;
  const arrowH = ios ? 44 : 64;
  const arrowFilter = ios
    ? "drop-shadow(0 0 6px rgba(0,255,136,0.55))"
    : "drop-shadow(0 0 12px rgba(0,255,136,0.65)) drop-shadow(0 0 28px rgba(139,92,246,0.35))";

  return (
    <div className="fixed inset-0 z-[310] pointer-events-none" aria-hidden>
      <div
        className="absolute rounded-[14px]"
        style={{
          top: ringTop,
          left: ringLeft,
          width: ringW,
          height: ringH,
          border: ringBorder,
          boxShadow: ringShadow,
          background: ringBg,
          transition: ringTransition,
          animation: ringAnim,
        }}
      />
      <div
        className="absolute flex flex-col items-center"
        style={{
          left: targetCenterX,
          bottom: finalBottom,
          transform: "translateX(-50%)",
          transition: ios
            ? "left 900ms cubic-bezier(0.22,1,0.36,1), bottom 900ms cubic-bezier(0.22,1,0.36,1)"
            : "left 700ms cubic-bezier(0.22,1,0.36,1), bottom 700ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <svg
          width={arrowW}
          height={arrowH}
          viewBox="0 0 44 64"
          fill="none"
          style={{
            filter: arrowFilter,
            // iOS: no bobbing animation — keep arrow static to avoid layout/composite churn.
            animation: ios ? "none" : "arrowBob 1.6s ease-in-out infinite",
          }}
        >
          <defs>
            <linearGradient id="arrowGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00FF88" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          <rect x="20" y="4" width="4" height="40" rx="2" fill="url(#arrowGrad)" />
          <path
            d="M22 60 L6 40 L18 40 L18 32 L26 32 L26 40 L38 40 Z"
            fill="url(#arrowGrad)"
          />
        </svg>
      </div>
    </div>
  );
};

const DemoOnboarding = () => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [stepIdx, setStepIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [viewport, setViewport] = useState({ height: 0, offsetTop: 0 });
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>("idle");
  const [closing, setClosing] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const engineIdRef = useRef(0);
  const pausedRef = useRef(false);
  const mutedRef = useRef(false);
  const userPausedRef = useRef(false);
  const { fetchAudio } = useTtsAudio();

  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const closeWithFade = useCallback((toPhase: Phase = "done") => {
    engineIdRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    userPausedRef.current = false;
    setVoiceStatus("idle");
    setRect(null);
    setClosing(true);

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = window.setTimeout(() => {
      setPhase(toPhase);
      setStepIdx(0);
      setClosing(false);
      closeTimerRef.current = null;
      if (toPhase === "done" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("orbit:onboarding-finished"));
      }
    }, 360);
  }, []);

  const finishTour = useCallback(() => {
    closeWithFade("done");
  }, [closeWithFade]);

  // First-visit trigger
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY) === "seen") return;
    const t = setTimeout(() => setPhase("card"), 2800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    return () => {
      engineIdRef.current += 1;
      audioRef.current?.pause();
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
      document.body.classList.remove("orbit-onboarding-open");
    };
  }, []);

  useEffect(() => {
    if (phase !== "card") {
      document.body.classList.remove("orbit-onboarding-open");
      return;
    }
    document.body.classList.add("orbit-onboarding-open");
    const updateViewport = () => {
      const vv = window.visualViewport;
      setViewport({
        height: vv?.height ?? window.innerHeight,
        offsetTop: vv?.offsetTop ?? 0,
      });
    };
    updateViewport();
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        sessionStorage.setItem(STORAGE_KEY, "seen");
        setVoiceMode(false);
        closeWithFade("done");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("orbit-onboarding-open");
    };
  }, [phase, closeWithFade]);

  const step = SCRIPT[stepIdx];

  // Switch dashboard view to match step
  useEffect(() => {
    if (phase !== "tour" || !step?.view) return;
    window.dispatchEvent(new CustomEvent("orbit:set-dashboard-view", { detail: step.view }));
  }, [phase, step?.view]);

  // Multi-target cycling index (driven by the orchestrator below).
  const [targetIdx, setTargetIdx] = useState(0);


  /** Single orchestrated tutorial controller: view/action -> scroll/measure -> voice -> target movement -> advance. */
  useEffect(() => {
    if (phase !== "tour" || !step) return;
    const ios = isIOS();
    const engineId = ++engineIdRef.current;
    const targets: (string | null)[] = step.targets && step.targets.length
      ? step.targets
      : [step.selector ?? null];

    let cancelled = false;
    let advancing = false;
    let fallbackActive = false;
    let narrationHasEnded = false;
    let expectedPause = false;
    let cleanupFns: Array<() => void> = [];
    const pushTimer = (id: number) => cleanupFns.push(() => window.clearTimeout(id));
    const pushInterval = (id: number) => cleanupFns.push(() => window.clearInterval(id));
    const isCurrent = () => !cancelled && engineIdRef.current === engineId;

    const measure = (idx: number) => {
      if (!isCurrent()) return;
      const sel = targets[idx] ?? targets[0];
      if (!sel) { setRect(null); return; }
      const el = document.querySelector(sel) as HTMLElement | null;
      if (!el) { setRect(null); return; }
      requestAnimationFrame(() => {
        if (isCurrent()) setRect(el.getBoundingClientRect());
      });
    };

    const ensureInView = (el: HTMLElement, onDone: () => void, allowScroll = true) => {
      if (!isCurrent()) return;
      const r = el.getBoundingClientRect();
      const viewport = getViewportMetrics();
      const vh = viewport.height;
      const topInset = (ios ? 84 : 64) + viewport.offsetTop;
      const bottomInset = ios ? 188 : 160;
      const outOfView = r.top < topInset || r.bottom > vh - bottomInset;
      if (!allowScroll || !outOfView) { onDone(); return; }
      const desiredTop = window.scrollY + r.top - Math.max(topInset, (vh - bottomInset - r.height) / 2);
      window.scrollTo({ top: Math.max(0, desiredTop), behavior: ios ? "auto" : "smooth" });
      const t = window.setTimeout(() => { if (isCurrent()) onDone(); }, ios ? 260 : 520);
      pushTimer(t);
    };

    // 1) Reset to first target for the new step.
    setTargetIdx(0);
    setVoiceStatus(voiceMode && !muted ? "preparing" : "silent");
    let currentTargetIdx = 0;

    // 2) Fire the step's view + action so the right tab is mounted before we measure.
    const actionTimer = window.setTimeout(() => {
      if (!isCurrent()) return;
      try { step.action?.(); } catch (e) { console.warn("[demo-onboarding] step action failed", e); }
    }, 100);
    pushTimer(actionTimer);

    // 3) Scroll-to-top (if requested) then ensure-in-view then measure.
    const beginStep = () => {
      if (!isCurrent()) return;
      const sel = targets[0];
      const el = sel ? (document.querySelector(sel) as HTMLElement | null) : null;
      const onSettled = () => {
        if (!isCurrent()) return;
        measure(0);
        startNarration();
      };
      if (el) ensureInView(el, onSettled);
      else { setRect(null); onSettled(); }
    };

    if (step.scrollToTop) {
      window.scrollTo({ top: 0, behavior: ios ? "auto" : "smooth" });
      const t = window.setTimeout(beginStep, ios ? 260 : 520);
      pushTimer(t);
    } else {
      // small beat so view-switch / action can render
      const t = window.setTimeout(beginStep, ios ? 220 : 280);
      pushTimer(t);
    }

    // 4) Audio-driven narration + target cycling + auto-advance.
    const advance = () => {
      if (!isCurrent() || advancing) return;
      if (voiceMode && !mutedRef.current && !fallbackActive && !narrationHasEnded) return;
      advancing = true;
      if (stepIdx >= SCRIPT.length - 1) {
        finishTour();
      } else {
        setStepIdx((i) => Math.min(i + 1, SCRIPT.length - 1));
      }
    };

    const cycleTo = (i: number) => {
      if (!isCurrent()) return;
      const audio = audioRef.current;
      if (voiceMode && !fallbackActive && audio && !audio.ended && audio.paused && !userPausedRef.current) return;
      if (i === currentTargetIdx) return;
      currentTargetIdx = i;
      setTargetIdx(i);
      const sel = targets[i];
      const el = sel ? (document.querySelector(sel) as HTMLElement | null) : null;
      if (el) ensureInView(el, () => measure(i), !ios);
      else measure(i);
    };

    // Fallback duration estimate when audio is unavailable / muted.
    const estimatedDuration = Math.max(
      5000,
      Math.min(22000, Math.round(step.text.length * 62))
    );

    const startFallback = () => {
      fallbackActive = true;
      setVoiceStatus("silent");
      // Cycle through sub-targets evenly across the estimated duration.
      if (targets.length > 1) {
        const slice = estimatedDuration / targets.length;
        const id = window.setInterval(() => {
          if (!isCurrent() || pausedRef.current) return;
          const next = (currentTargetIdx + 1) % targets.length;
          cycleTo(next);
        }, Math.max(2200, slice));
        pushInterval(id);
      }
      const advTimer = window.setTimeout(() => {
        if (isCurrent() && !pausedRef.current) advance();
      }, estimatedDuration + 600);
      pushTimer(advTimer);
    };

    const startNarration = () => {
      if (!isCurrent()) return;
      if (!voiceMode || mutedRef.current) {
        startFallback();
        return;
      }
      (async () => {
        setVoiceStatus("loading");
        const url = await fetchAudio(step.text);
        if (!isCurrent()) return;
        if (!url) { setVoiceStatus("interrupted"); return; }
        const audio = new Audio(url);
        audio.preload = "auto";
        audio.setAttribute("playsinline", "true");
        audio.volume = 0.95;
        audio.muted = mutedRef.current;
        expectedPause = true;
        audioRef.current?.pause();
        expectedPause = false;
        audioRef.current = audio;

        // Drive target cycling from audio playback time (perfect sync).
        const onTime = () => {
          if (!isCurrent() || targets.length <= 1) return;
          const d = audio.duration;
          if (!d || !isFinite(d)) return;
          const ratio = Math.min(0.999, audio.currentTime / d);
          const idx = Math.min(targets.length - 1, Math.floor(ratio * targets.length));
          if (idx !== currentTargetIdx) cycleTo(idx);
        };
        const onEnded = () => {
          if (!isCurrent()) return;
          narrationHasEnded = true;
          setVoiceStatus("ended");
          const t = window.setTimeout(() => { if (isCurrent()) advance(); }, 650);
          pushTimer(t);
        };
        const onError = () => {
          if (!isCurrent()) return;
          setVoiceStatus("interrupted");
        };
        const onPause = () => {
          if (!isCurrent() || narrationHasEnded || expectedPause || userPausedRef.current) return;
          setVoiceStatus("interrupted");
          const resumeTimer = window.setTimeout(() => {
            if (!isCurrent() || narrationHasEnded || userPausedRef.current || mutedRef.current) return;
            audio.play().then(() => setVoiceStatus("playing")).catch(() => setVoiceStatus("interrupted"));
          }, ios ? 180 : 260);
          pushTimer(resumeTimer);
        };
        const onPlay = () => {
          if (isCurrent()) setVoiceStatus("playing");
        };
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("error", onError);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("play", onPlay);
        cleanupFns.push(() => {
          audio.removeEventListener("timeupdate", onTime);
          audio.removeEventListener("ended", onEnded);
          audio.removeEventListener("error", onError);
          audio.removeEventListener("pause", onPause);
          audio.removeEventListener("play", onPlay);
          expectedPause = true;
          audio.pause();
        });

        if (!pausedRef.current) {
          audio.play().then(() => setVoiceStatus("playing")).catch(() => {
            if (isCurrent()) setVoiceStatus("interrupted");
          });
        } else {
          setVoiceStatus("paused");
        }
      })();
    };

    // Recalculate on viewport changes (Safari toolbar collapse, rotation).
    const onResize = () => {
      const t = window.setTimeout(() => measure(currentTargetIdx), ios ? 120 : 0);
      pushTimer(t);
    };
    window.addEventListener("orientationchange", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("scroll", onResize);
    // On non-iOS, also track scroll for live tracking; on iOS we trust step-anchored measure.
    if (!ios) {
      window.addEventListener("scroll", onResize, true);
      const id = window.setInterval(() => measure(currentTargetIdx), 600);
      pushInterval(id);
    }
    cleanupFns.push(() => {
      window.removeEventListener("orientationchange", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("scroll", onResize);
      if (!ios) window.removeEventListener("scroll", onResize, true);
    });

    return () => {
      cancelled = true;
      cleanupFns.forEach((fn) => { try { fn(); } catch {} });
      cleanupFns = [];
    };
    // Re-run only for a new step or voice-mode boundary; pause/mute mutate refs/audio in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stepIdx, voiceMode]);

  // Pause/resume the live audio without restarting the step.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    userPausedRef.current = paused;
    if (paused) {
      setVoiceStatus("paused");
      a.pause();
    } else {
      a.play().then(() => setVoiceStatus("playing")).catch(() => setVoiceStatus("interrupted"));
    }
  }, [paused]);

  const startVoice = useCallback(async () => {
    sessionStorage.setItem(STORAGE_KEY, "seen");
    setVoiceMode(true);
    setPhase("tour");
    setStepIdx(0);
  }, []);

  const startSilent = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "seen");
    setVoiceMode(false);
    closeWithFade("done");
  }, [closeWithFade]);

  const skipTour = useCallback(() => closeWithFade("done"), [closeWithFade]);

  const replay = useCallback(() => {
    engineIdRef.current += 1;
    setStepIdx(0);
    setVoiceMode(true);
    setMuted(false);
    setPaused(false);
    userPausedRef.current = false;
    setVoiceStatus("preparing");
    setClosing(false);
    setPhase("tour");
  }, []);

  const resumeVoice = useCallback(() => {
    userPausedRef.current = false;
    setPaused(false);
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = mutedRef.current;
    audio.play().then(() => setVoiceStatus("playing")).catch(() => setVoiceStatus("interrupted"));
  }, []);

  const togglePause = useCallback(() => {
    setPaused((p) => {
      const next = !p;
      userPausedRef.current = next;
      if (next) {
        setVoiceStatus("paused");
        audioRef.current?.pause();
      } else {
        audioRef.current?.play().then(() => setVoiceStatus("playing")).catch(() => setVoiceStatus("interrupted"));
      }
      return next;
    });
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      mutedRef.current = next;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  }, []);

  const next = useCallback(() => {
    engineIdRef.current += 1;
    userPausedRef.current = false;
    audioRef.current?.pause();
    setVoiceStatus("preparing");
    if (stepIdx >= SCRIPT.length - 1) {
      finishTour();
      return;
    }
    setStepIdx((i) => Math.min(i + 1, SCRIPT.length - 1));
  }, [finishTour, stepIdx]);

  const compactControls = typeof window !== "undefined" && window.innerWidth <= 360;
  const statusLabel = compactControls
    ? voiceStatus === "interrupted"
      ? "Resume"
      : voiceStatus === "paused"
      ? "Paused"
      : "Voice"
    : voiceStatus === "loading" || voiceStatus === "preparing"
    ? "Preparing voice"
    : voiceStatus === "interrupted"
    ? "Tap to resume"
    : voiceStatus === "paused"
    ? "Paused"
    : voiceStatus === "playing"
    ? "Voice active"
    : "Guided tour";

  return (
    <>
      <style>{`
        body.orbit-onboarding-open {
          overflow: hidden !important;
        }
        body.orbit-onboarding-open .cw-bubble,
        body.orbit-onboarding-open .cw-tooltip,
        body.orbit-onboarding-open .cw-window {
          display: none !important;
        }
        .orbit-mobile-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .orbit-mobile-scroll::-webkit-scrollbar {
          display: none;
        }
        @keyframes tourGlowPulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(0,255,136,0.25), 0 0 26px 4px rgba(0,255,136,0.30), 0 0 60px 10px rgba(139,92,246,0.18); }
          50%      { box-shadow: 0 0 0 1px rgba(0,255,136,0.45), 0 0 42px 8px rgba(0,255,136,0.48), 0 0 80px 14px rgba(139,92,246,0.30); }
        }
        @keyframes arrowBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }
        @keyframes onboardingCardIn {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes captionIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Welcome card */}
      {phase === "card" && (
        <div
          className="fixed inset-x-0 z-[10010] flex items-start justify-center overflow-x-hidden overflow-y-auto overscroll-contain px-4 sm:items-center"
          style={{
            top: viewport.offsetTop || 0,
            height: viewport.height ? `${viewport.height}px` : "100dvh",
            background: "rgba(4,8,14,0.55)",
            backdropFilter: isIOS() ? "blur(4px)" : "blur(8px)",
            WebkitBackdropFilter: isIOS() ? "blur(4px)" : "blur(8px)",
            opacity: closing ? 0 : 1,
            transition: "opacity 360ms ease",
            pointerEvents: closing ? "none" : "auto",
            paddingBottom: "max(96px, env(safe-area-inset-bottom))",
            paddingTop: "max(16px, env(safe-area-inset-top))",
            WebkitOverflowScrolling: "touch",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="orbit-mobile-scroll relative my-auto w-full max-w-[420px] overflow-x-hidden overflow-y-auto rounded-[20px]"
            style={{
              maxHeight: viewport.height
                ? `${Math.max(320, viewport.height - 118)}px`
                : "calc(100dvh - max(120px, env(safe-area-inset-bottom)))",
              background:
                "linear-gradient(160deg, rgba(14,18,28,0.92) 0%, rgba(10,14,24,0.94) 60%, rgba(16,12,30,0.92) 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow:
                "0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,255,136,0.05) inset, 0 -1px 0 rgba(255,255,255,0.04) inset",
              animation: "onboardingCardIn 520ms cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <div
              className="absolute -top-24 -left-16 w-64 h-64 pointer-events-none rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(0,255,136,0.25), transparent 70%)",
                filter: "blur(40px)",
              }}
            />
            <div
              className="absolute -bottom-24 -right-16 w-64 h-64 pointer-events-none rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(139,92,246,0.28), transparent 70%)",
                filter: "blur(44px)",
              }}
            />

            <button
              onClick={startSilent}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 rounded-md p-1.5 transition-colors"
              style={{ color: "rgba(255,255,255,0.5)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.9)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
            >
              <X size={16} />
            </button>

            <div className="relative px-6 pt-7 pb-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="relative flex h-[7px] w-[7px]">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full opacity-60"
                    style={{ background: "#00FF88", animation: "ping 2.4s cubic-bezier(0,0,0.2,1) infinite" }}
                  />
                  <span
                    className="relative inline-flex h-[7px] w-[7px] rounded-full"
                    style={{ background: "#00FF88", boxShadow: "0 0 8px 2px rgba(0,255,136,0.55)" }}
                  />
                </span>
                <span
                  className="text-[10px] font-semibold tracking-[0.22em] uppercase"
                  style={{ color: "#00FF88" }}
                >
                  AI Onboarding
                </span>
              </div>

              <h2
                className="text-[19px] sm:text-[21px] font-semibold leading-snug tracking-[-0.01em] mb-2"
                style={{ color: "rgba(255,255,255,0.97)" }}
              >
                Welcome to the OrbitRank Demo Workspace
              </h2>
              <p
                className="text-[13.5px] leading-relaxed mb-6"
                style={{ color: "rgba(255,255,255,0.62)" }}
              >
                Explore freely or experience a guided AI walkthrough of the platform.
              </p>

              <div className="flex flex-col gap-2.5">
                <button
                  onClick={startVoice}
                  className="group relative flex items-center justify-center gap-2 rounded-[12px] px-4 py-3 text-[13.5px] font-semibold transition-all"
                  style={{
                    background: "linear-gradient(135deg, #00E589 0%, #00B86B 100%)",
                    color: "#052e16",
                    boxShadow:
                      "0 0 22px rgba(0,229,137,0.38), 0 1px 2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.25)",
                    letterSpacing: "-0.01em",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.filter = "brightness(1.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.filter = "brightness(1)";
                  }}
                >
                  <Sparkles size={15} />
                  Explore With Voice
                </button>
                <button
                  onClick={startSilent}
                  className="rounded-[12px] px-4 py-3 text-[13px] font-medium transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.78)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    letterSpacing: "-0.01em",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.95)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.78)";
                  }}
                >
                  Explore Freely
                </button>
              </div>

              <p
                className="mt-4 text-center text-[11px]"
                style={{ color: "rgba(255,255,255,0.42)" }}
              >
                Voice walkthrough takes less than 30 seconds.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tour — arrow + caption, NO background blur/spotlight */}
      {phase === "tour" && step && (
        <div
          style={{
            opacity: closing ? 0 : 1,
            transition: "opacity 360ms ease",
            pointerEvents: closing ? "none" : "auto",
          }}
        >
          <ArrowHighlight rect={rect} />

          {/* Minimal controls bar — no narration text */}
          <div
            className="fixed left-3 z-[320] w-[calc(100%-24px)] max-w-[480px] sm:left-1/2 sm:-translate-x-1/2"
            style={{
              bottom: isIOS() ? "max(74px, calc(env(safe-area-inset-bottom) + 74px))" : "max(20px, env(safe-area-inset-bottom))",
            }}
          >
            <div
              key={step.id}
              className="rounded-[16px] overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, rgba(12,16,26,0.94) 0%, rgba(10,14,22,0.96) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow:
                  "0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,255,136,0.06) inset",
                animation: "captionIn 380ms cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              {/* Progress bar */}
              <div className="h-[2px] w-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${((stepIdx + 1) / SCRIPT.length) * 100}%`,
                    background: "linear-gradient(90deg, #00FF88, #8B5CF6)",
                    boxShadow: "0 0 8px rgba(0,255,136,0.6)",
                  }}
                />
              </div>

              {/* Controls */}
              <div
                className="flex items-center justify-between px-3 py-2.5"
                style={{ background: "rgba(255,255,255,0.02)" }}
              >
                <div className="flex items-center gap-1">
                  {voiceMode && (
                    <>
                      <ControlBtn onClick={togglePause} label={paused ? "Play" : "Pause"}>
                        {paused ? <Play size={14} /> : <Pause size={14} />}
                      </ControlBtn>
                      <ControlBtn onClick={toggleMute} label={muted ? "Unmute" : "Mute"}>
                        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                      </ControlBtn>
                      <button
                        type="button"
                        onClick={voiceStatus === "interrupted" ? resumeVoice : undefined}
                        className="ml-0.5 rounded-full px-1.5 py-1 text-[9.5px] font-semibold sm:ml-1 sm:px-2 sm:text-[10px]"
                        style={{
                          color: voiceStatus === "interrupted" ? "#00FF88" : "rgba(255,255,255,0.55)",
                          background: voiceStatus === "interrupted" ? "rgba(0,255,136,0.1)" : "rgba(255,255,255,0.04)",
                          border: voiceStatus === "interrupted" ? "1px solid rgba(0,255,136,0.25)" : "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        {statusLabel}
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={skipTour}
                    className="rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors sm:px-3 sm:text-[11.5px]"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.9)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
                  >
                    Skip
                  </button>
                  <button
                    onClick={next}
                    className="rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-all sm:px-3 sm:text-[11.5px]"
                    style={{
                      background: "rgba(0,255,136,0.12)",
                      color: "#00FF88",
                      border: "1px solid rgba(0,255,136,0.25)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(0,255,136,0.18)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(0,255,136,0.12)";
                    }}
                  >
                    {stepIdx >= SCRIPT.length - 1 ? "Finish" : "Next →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replay pill */}
      {phase === "done" && (
        <button
          onClick={replay}
          className="fixed z-[200] flex items-center gap-2 rounded-full px-3.5 py-2 text-[11.5px] font-medium transition-all"
          style={{
            bottom: "max(18px, env(safe-area-inset-bottom))",
            right: 18,
            background: "rgba(12,16,26,0.85)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(0,255,136,0.22)",
            color: "rgba(255,255,255,0.85)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4), 0 0 14px rgba(0,255,136,0.12)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 14px 36px rgba(0,0,0,0.45), 0 0 22px rgba(0,255,136,0.22)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 10px 30px rgba(0,0,0,0.4), 0 0 14px rgba(0,255,136,0.12)";
          }}
        >
          <RotateCcw size={13} style={{ color: "#00FF88" }} />
          Replay walkthrough
        </button>
      )}
    </>
  );
};

const ControlBtn = ({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="flex items-center justify-center w-7 h-7 rounded-md transition-colors"
    style={{ color: "rgba(255,255,255,0.7)" }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(255,255,255,0.06)";
      e.currentTarget.style.color = "rgba(255,255,255,0.95)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = "rgba(255,255,255,0.7)";
    }}
  >
    {children}
  </button>
);

export default DemoOnboarding;
